import { useState, useMemo } from 'react';
import { Table, Button, Form, Tabs, Tab } from 'react-bootstrap';
import { FaTimes, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { useParams, Link } from 'react-router-dom';
import { useGetOrdersQuery } from '../../slices/ordersApiSlice';
import SummaryHeader from '../../components/SummaryHeader.jsx';

const UserDetailScreen = () => {
    const { id: userId } = useParams();
    const { data: orders, isLoading, error } = useGetOrdersQuery();

    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    // const itemsPerPage = 50;
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [activeTab, setActiveTab] = useState('orders');

    // FILTER ORDERS OF THIS USER
    const userOrders = useMemo(() => {
        if (!orders) return [];
        return orders.filter((o) => o.user?._id === userId);
    }, [orders, userId]);

    // USER NAME (from order.user.name)
    const userName = userOrders[0]?.user?.name || 'Onbekend';

    // USER SUMMARY TOTALS
    const summary = useMemo(() => {
        let totalPaid = 0;
        let totalUnpaid = 0;
        let totalBooks = 0;
        let deliveredCount = 0;
        let deletedItemsPrice = 0;

        userOrders.forEach((o) => {
            const itemsPrice = Number(o.itemsPrice || 0);

            if (o.isPaid) totalPaid += itemsPrice;
            else totalUnpaid += itemsPrice;

            if (Array.isArray(o.orderItems)) {
                totalBooks += o.orderItems.length;
            }

            if (o.isDelivered) deliveredCount += 1;

            if (!o.isDelivered && Array.isArray(o.removedItems)) {
                o.removedItems.forEach((item) => {
                    deletedItemsPrice += Number(
                        item.accepted_price ??
                        item.offer_price ??
                        item.price ??
                        0
                    );
                });
            }
        });

        return {
            orderCount: userOrders.length,
            totalPaid,
            totalUnpaid,
            totalBooks,
            deliveredCount,
            deletedItemsPrice,
        };
    }, [userOrders]);

    // FILTER + SORT
    const filteredSortedOrders = useMemo(() => {
        let list = [...userOrders];

        // SEARCH
        // SEARCH
        if (searchQuery) {
        const q = searchQuery.toLowerCase();

        list = list.filter((o) => {
            const dateMatch = o.createdAt?.substring(0, 10).includes(q);

            const activeItemMatch = Array.isArray(o.orderItems)
            ? o.orderItems.some((item) =>
                item.isbn?.toString().toLowerCase().includes(q)
                )
            : false;

            const deletedItemMatch = Array.isArray(o.removedItems)
            ? o.removedItems.some((item) =>
                item.isbn?.toString().toLowerCase().includes(q)
                )
            : false;

            return dateMatch || activeItemMatch || deletedItemMatch;
        });
        }

        // SORTING
        list.sort((a, b) => {
            const { key, direction } = sortConfig;
            let valA, valB;

            switch (key) {
                case 'id':
                valA = a._id;
                valB = b._id;
                break;
                case 'date':
                valA = new Date(a.createdAt);
                valB = new Date(b.createdAt);
                break;
                case 'total':
                valA = Number(a.itemsPrice || 0);
                valB = Number(b.itemsPrice || 0);
                break;
                case 'books':
                valA = a.orderItems.reduce((s, item) => s + Number(item.qty || 0), 0);
                valB = b.orderItems.reduce((s, item) => s + Number(item.qty || 0), 0);
                break;
                case 'paid':
                valA = a.isPaid;
                valB = b.isPaid;
                break;
                case 'delivered':
                valA = a.isDelivered;
                valB = b.isDelivered;
                break;
                default:
                valA = 0;
                valB = 0;
            }

            if (valA < valB) return direction === 'asc' ? -1 : 1;
            if (valA > valB) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        return list;
    }, [userOrders, searchQuery, sortConfig]);

    const pageCount = Math.ceil(filteredSortedOrders.length / itemsPerPage);
    const paginatedOrders = filteredSortedOrders.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleSort = (key) => {
        setSortConfig((prev) => {
        if (prev.key === key) {
            return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        return { key, direction: 'asc' };
        });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort />;
        return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
    };

    const allRemovedItems = useMemo(() => {
        const list = [];

        userOrders.forEach((order) => {
            order.removedItems?.forEach((item) => {
            list.push({
                ...item,
                orderId: order._id,
                date: order.createdAt?.substring(0, 10),
            });
            });
        });

        return list;
    }, [userOrders]);

    return (
        <>
            {/* HEADER */}
            <h1 className="display-6 display-md-5 text-center">Bestellingen van {userName}</h1>
            <div className="display-9 display-md-5 text-md-end">Gebruiker ID: {userId}</div>

            <SummaryHeader
                totalPaid={summary.totalPaid}
                totalUnpaid={summary.totalUnpaid}
                totalBooks={summary.totalBooks}
                totalOrders={summary.orderCount}
                deliveredCount={summary.deliveredCount}
                userCount={summary.orderCount}
                deletedItemsPrice={summary.deletedItemsPrice}
            />


            {/* SEARCH BAR */}
            <div
            className="my-3 mx-auto"
            style={{
                maxWidth: "600px",
                width: "100%",
            }}
            >
            <Form.Control
                type="text"
                placeholder="Zoek op Order Datum of ISBN..."
                value={searchQuery}
                onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
                }}
                style={{
                borderRadius: "12px",
                padding: "12px 16px",
                border: "2px solid #03337cff",
                boxShadow: "0 0 10px rgba(3, 96, 86, 0.67)",
                fontSize: "16px",
                }}
            />
            </div>

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error?.data?.message || error.error}</Message>
            ) : userOrders.length === 0 ? (
                <Message variant="info">Geen bestellingen gevonden.</Message>
            ) : (
                <>
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                        <Tab eventKey="orders" title={`Bestellingen (${userOrders.length})`}>
                            <Table striped bordered hover responsive>
                                <thead>
                                <tr>
                                    {/* <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
                                    Order ID {renderSortIcon('id')}
                                    </th> */}
                                    <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                                    Datum {renderSortIcon('date')}
                                    </th>
                                    <th onClick={() => handleSort('total')} style={{ cursor: 'pointer' }}>
                                    Totaal {renderSortIcon('total')}
                                    </th>
                                    <th>Totaal Verwijderd</th>
                                    <th onClick={() => handleSort('books')} style={{ cursor: 'pointer' }}>
                                    Boeken {renderSortIcon('books')}
                                    </th>
                                    <th>Deleted</th>
                                    <th onClick={() => handleSort('paid')} style={{ cursor: 'pointer' }}>
                                    Betaald {renderSortIcon('paid')}
                                    </th>
                                    <th onClick={() => handleSort('delivered')} style={{ cursor: 'pointer' }}>
                                    Geleverd {renderSortIcon('delivered')}
                                    </th>
                                    <th>Detail</th>
                                </tr>
                                </thead>
                                <tbody>
                                {paginatedOrders.map((order) => {
                                    const deletedItemsCount = order.removedItems?.length || 0;

                                    const deletedItemsPrice = order.removedItems?.reduce(
                                    (sum, item) =>
                                        sum + Number(item.accepted_price ?? item.offer_price ?? item.price ?? 0),
                                    0
                                    );

                                    const hasDeletedMatch =
                                        searchQuery &&
                                        order.removedItems?.some((item) =>
                                            item.isbn?.toString().toLowerCase().includes(searchQuery.toLowerCase())
                                    );

                                    return (
                                    <tr
                                        key={order._id}
                                        className={`
                                            ${!order.isDelivered ? 'table-warning' : ''}
                                            ${hasDeletedMatch ? 'table-danger' : ''}
                                        `}
                                    >
                                        {/* <td>{order._id}</td> */}
                                        <td>{order.createdAt?.substring(0, 10)}</td>
                                        <td>€ {Number(order.itemsPrice).toFixed(2)}</td>
                                        <td>
                                        € {deletedItemsPrice.toFixed(2)}
                                        </td>
                                        <td>{order.orderItems.length}</td>
                                        <td>
                                        {/* {deletedItemsCount > 0 && (
                                            <span className="badge bg-danger me-1">{deletedItemsCount}</span>
                                        )} */}
                                        {deletedItemsCount > 0 ? deletedItemsCount : '—'}
                                        </td>
                                        <td>
                                        {order.isPaid ? (
                                            order.paidAt?.substring(0, 10)
                                        ) : (
                                            <FaTimes style={{ color: 'red' }} />
                                        )}
                                        </td>
                                        <td>
                                        {order.isDelivered ? (
                                            order.deliveredAt?.substring(0, 10)
                                        ) : (
                                            <FaTimes style={{ color: 'red' }} />
                                        )}
                                        </td>
                                        <td>
                                        <Button as={Link} to={`/order/${order._id}`} className="btn-sm">
                                            Bekijk
                                        </Button>
                                        </td>
                                    </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                            {/* PAGINATION */}
                            <div className="d-flex justify-content-between align-items-center">
                                <Button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage((p) => p - 1)}
                                >
                                Vorige
                                </Button>

                                <span>
                                Pagina {currentPage} van {pageCount}
                                </span>

                                <Button
                                disabled={currentPage === pageCount}
                                onClick={() => setCurrentPage((p) => p + 1)}
                                >
                                Volgende
                                </Button>

                                {/* Right: page size selector */}
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted">Per pagina:</span>
                                    <Form.Select
                                    size="sm"
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1); // important
                                    }}
                                    style={{ width: '90px' }}
                                    >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    </Form.Select>
                                </div>
                                    <small className="text-muted">
                                        Toon {(currentPage - 1) * itemsPerPage + 1}–
                                        {Math.min(currentPage * itemsPerPage, filteredSortedOrders.length)}
                                        {' '}van {filteredSortedOrders.length} bestellingen
                                    </small>
                            </div>
                        </Tab>
                        <Tab eventKey="deleted" title={`Verwijderd (${allRemovedItems.length})`}>
                            {allRemovedItems.length ? (
                                <Table striped bordered hover responsive>
                                    <thead>
                                    <tr>
                                        <th>Datum</th>
                                        <th>ISBN</th>
                                        <th>Titel</th>
                                        <th>Prijs</th>
                                        <th>Order</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {allRemovedItems.map((it) => (
                                        <tr key={it._id}>
                                        <td>{it.date}</td>
                                        <td>{it.isbn}</td>
                                        <td>{it.title}</td>
                                        <td>
                                            € {Number(it.accepted_price ?? it.offer_price ?? it.price ?? 0).toFixed(2)}
                                        </td>
                                        <td>
                                            <Button as={Link} to={`/order/${it.orderId}`} className="btn-sm">
                                            Bekijk
                                            </Button>
                                        </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            )
                            : (
                                <Message variant="info">Geen verwijderde items gevonden.</Message>
                            )}
                        </Tab>
                    </Tabs>

                </>
            )}
        </>
    );
};

export default UserDetailScreen;